/* ============================================
   ACKNOWLEDGEMENTS PAGE
   The Creative Orchestra - GEMINI Framework
   Gratitude to those who made this journey possible
   ============================================ */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, 
  BookOpen,
  ArrowRight,
  X,
  Calendar,
} from 'lucide-react';
import { ScrollReveal } from '@components/common';
import acknowledgements from '@data/acknowledgements.json';
import { SITE_INFO } from '@utils/constants';

// Add global styles to prevent image download/copy
const imageProtectionStyles = `
  .acknowledgements-page img {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    pointer-events: none;
  }
  
  .acknowledgements-page {
    -webkit-touch-callout: none;
  }
  
  /* Prevent print of images */
  @media print {
    .acknowledgements-page img {
      display: none !important;
    }
  }
`;

/**
 * Acknowledgements Page Component
 */
const AcknowledgementsPage = () => {
  useEffect(() => {
    document.title = `Acknowledgements | ${SITE_INFO.title}`;

    // Prevent right-click context menu on images
    const preventContextMenu = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
      }
    };

    // Prevent drag and drop of images
    const preventDragStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('dragstart', preventDragStart);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('dragstart', preventDragStart);
    };
  }, []);

  return (
    <div className="min-h-screen py-20 select-none acknowledgements-page"
      onContextMenu={(e) => {
        // Additional protection at component level
        if (e.target.tagName === 'IMG') {
          e.preventDefault();
        }
      }}
    >
      {/* Inject protection styles */}
      <style>{imageProtectionStyles}</style>
      
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <ScrollReveal>
          <header className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <motion.div
                className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500/30 to-red-500/20
                  flex items-center justify-center border-2 border-pink-500/20"
                animate={{
                  boxShadow: [
                    '0 0 20px rgba(236, 72, 153, 0.3)',
                    '0 0 40px rgba(236, 72, 153, 0.5)',
                    '0 0 20px rgba(236, 72, 153, 0.3)',
                  ],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Heart className="w-10 h-10 text-pink-400 fill-pink-400" />
              </motion.div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
              {acknowledgements.title}
            </h1>
            <p className="text-xl text-gray-400 mb-6 whitespace-pre-line">
              {acknowledgements.subtitle}
            </p>
            
            {/* Duration */}
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>
                {acknowledgements.duration.start} — {acknowledgements.duration.end}
              </span>
              <span className="text-indigo-400 font-semibold">
                ({acknowledgements.duration.days} days)
              </span>
            </div>
          </header>
        </ScrollReveal>

        {/* Introduction */}
        <ScrollReveal delay={0.1}>
          <div className="mb-16 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/5
            border border-white/10">
            <p className="text-lg text-gray-300 leading-relaxed italic">
              {acknowledgements.introduction}
            </p>
          </div>
        </ScrollReveal>

        {/* Groups */}
        <div className="space-y-16">
          {acknowledgements.groups.map((group, groupIndex) => (
            <AcknowledgementGroup 
              key={group.id} 
              group={group} 
              index={groupIndex} 
            />
          ))}
        </div>

        {/* Final Note */}
        <ScrollReveal delay={0.2}>
          <div className="mt-16 text-center p-8 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-pink-500/5
            border border-white/10">
            <p className="text-2xl font-display text-white mb-4">
              Wow! It has been a ride.
            </p>
            <Link to="/novel">
              <motion.button
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full
                  bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium"
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(129, 140, 248, 0.5)' }}
                whileTap={{ scale: 0.95 }}
              >
                <BookOpen className="w-5 h-5" />
                Read the Full Story
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
};

/**
 * Acknowledgement Group Component
 */
const AcknowledgementGroup = ({ group, index }) => {
  return (
    <ScrollReveal delay={index * 0.1}>
      <section>
        <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-8 
          text-center md:text-left">
          {group.title}
        </h2>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {group.people.map((person, personIndex) => (
            <PersonCard 
              key={person.id} 
              person={person} 
              index={personIndex} 
            />
          ))}
        </div>
      </section>
    </ScrollReveal>
  );
};

/**
 * Person Card with Hover Modal
 */
const PersonCard = ({ person, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Person Card */}
      <motion.div
        className="text-center cursor-pointer"
        whileHover={{ y: -4 }}
      >
        {/* Profile Photo */}
        <div className="relative w-24 h-24 mx-auto mb-3">
          <div className="w-full h-full rounded-full overflow-hidden border-2 border-white/10
            bg-gradient-to-br from-indigo-500/20 to-purple-500/20">
            <img
              src={person.image}
              alt={person.name}
              className="w-full h-full object-cover select-none pointer-events-none"
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `
                  <div class="w-full h-full flex items-center justify-center text-2xl font-bold text-white">
                    ${person.name.charAt(0)}
                  </div>
                `;
              }}
            />
          </div>
          
          {/* Decorative ring on hover */}
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-pink-500/0"
            animate={isHovered ? { 
              borderColor: 'rgba(236, 72, 153, 0.5)',
              scale: 1.1,
            } : {
              borderColor: 'rgba(236, 72, 153, 0)',
              scale: 1,
            }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Name */}
        <h3 className="text-sm font-semibold text-white mb-1 line-clamp-2">
          {person.name}
        </h3>

        {/* Designation */}
        <p className="text-xs text-gray-500 line-clamp-2">
          {person.designation}
        </p>
      </motion.div>

      {/* Hover Description Modal */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64"
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="relative p-4 rounded-xl bg-gray-900 border border-white/20
              shadow-2xl backdrop-blur-sm">
              {/* Arrow */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 
                bg-gray-900 border-r border-b border-white/20 rotate-45" />
              
              {/* Content */}
              <div className="relative z-10">
                <h4 className="text-sm font-semibold text-white mb-1">
                  {person.name}
                </h4>
                <p className="text-xs text-indigo-400 mb-2">
                  {person.designation}
                </p>
                <p className="text-xs text-gray-400 leading-relaxed">
                  {person.description}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AcknowledgementsPage;

